import { MapUtils } from '../../utility/map-utils.js';
import { Country } from '../serializable/country.js';
import { PhoneNumber } from './phone-number.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { Address } from './address.js';
import { GeoCoordinates } from './geo-coordinates.js';
import { VersionRange } from '../core/version-range.js';
import { BusinessProfile } from './business-profile.js';
import { ContactComponent } from '../../components/misc/contact-component.js';
import { BusinessLocationComponent } from '../../components/business/business-location-component.js';
import { LocalizedString } from './localized-string.js';
import { getLanguageOfCountry, Language } from './language.js';
import { EventProfile } from './event-profile.js';
import { LocalizedNameComponent } from '../../components/point-of-interest/localized-name-component.js';
import { PlaceComponent } from '../../components/events/place-component.js';
import { LocationComponent } from '../../components/events/location-component.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { DateSpan } from './date-span.js';
import { DateSpanComponent } from '../../components/misc/date-span-component.js';
import { BusinessModule } from './business-module.js';

export class ContactDetails extends DataModel {
  static readonly TYPE_ID: string = 'fa13268f25f445c98aa5394b962f6f10';
  static readonly SCHEMA_VERSION: number = 5;

  private constructor(
    public readonly names: LocalizedString,
    public readonly country: Country,
    public readonly language: Language,
    public readonly timezone: TimeZoneRegion,
    public readonly phoneNumber: PhoneNumber | null,
    public readonly address: Address | null,
    public readonly coordinates: GeoCoordinates | null,
    public readonly webRoute: string | null,
    public readonly dateSpan: DateSpan | null,
  ) {
    super();
  }

  get $typeId(): string {
    return ContactDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(3, ContactDetails.SCHEMA_VERSION);
  }

  static create(opts: {
    names: LocalizedString,
    country: Country,
    language: Language,
    timezone: TimeZoneRegion,
    phoneNumber: PhoneNumber | null,
    address: Address | null,
    coordinates: GeoCoordinates | null,
    webRoute: string | null,
    dateSpan: DateSpan | null,
  }) {
    return new ContactDetails(
        opts.names,
        opts.country,
        opts.language,
        opts.timezone,
        opts.phoneNumber,
        opts.address,
        opts.coordinates,
        opts.webRoute,
        opts.dateSpan
    );
  }

  static forCalendarOrder(opts: {
    profile: BusinessProfile,
    dateSpan: DateSpan | null,
  }) {
    const contactComponent = opts.profile.mustFindComponentByTypeId<ContactComponent>(
        ContactComponent.TYPE_ID
    );

    const locationComponent = opts.profile.mustFindComponentByTypeId<BusinessLocationComponent>(
        BusinessLocationComponent.TYPE_ID
    );

    return new ContactDetails(
        opts.profile.names,
        opts.profile.country,
        opts.profile.language,
        opts.profile.timezone,
        contactComponent.phoneNumber?.clone() ?? null,
        locationComponent.location.address?.clone() ?? null,
        locationComponent.location.coordinates?.clone() ?? null,
        opts.profile.webRoute,
        opts.dateSpan
    );
  }

  static forTicketOrProductOrder(opts: {
    profile: EventProfile,
  }) {
    const localizedNameComponent = opts.profile.mustFindComponentByTypeId<LocalizedNameComponent>(
        LocalizedNameComponent.TYPE_ID
    );

    const locationComponent = opts.profile.findComponentByTypeId<LocationComponent>(
        LocationComponent.TYPE_ID
    );

    const placeComponent = opts.profile.findComponentByTypeId<PlaceComponent>(
        PlaceComponent.TYPE_ID
    );

    const dateComponent = opts.profile.findComponentByTypeId<DateSpanComponent>(
        DateSpanComponent.TYPE_ID
    );

    const mapLocation = locationComponent?.mapLocation ?? placeComponent?.place.mapLocation;

    return new ContactDetails(
        localizedNameComponent.names.clone(),
        opts.profile.country,
        opts.profile.language,
        opts.profile.timezone,
        null,
        mapLocation?.address?.clone() ?? null,
        mapLocation?.coordinates?.clone() ?? null,
        opts.profile.webRoute,
        dateComponent?.dateSpan ?? null,
    );
  }

  static forBusiness(opts: {
    business: BusinessModule,
  }) {
    return new ContactDetails(
        LocalizedString.localized(
            new Map<Language, string>([[Language.English, opts.business.name]])),
        opts.business.country,
        opts.business.language,
        opts.business.timezone,
        null,
        null,
        null,
        null,
        null,
    );
  }

  static forThirdPartyTicket(opts: {
    profile: EventProfile,
  }) {
    const localizedNameComponent = opts.profile.mustFindComponentByTypeId<LocalizedNameComponent>(
        LocalizedNameComponent.TYPE_ID
    );

    const locationComponent = opts.profile.findComponentByTypeId<LocationComponent>(
        LocationComponent.TYPE_ID
    );

    const placeComponent = opts.profile.findComponentByTypeId<PlaceComponent>(
        PlaceComponent.TYPE_ID
    );

    const dateComponent = opts.profile.findComponentByTypeId<DateSpanComponent>(
        DateSpanComponent.TYPE_ID
    );

    const mapLocation = locationComponent?.mapLocation ?? placeComponent?.place.mapLocation;

    return new ContactDetails(
        localizedNameComponent.names.clone(),
        opts.profile.country,
        opts.profile.language,
        opts.profile.timezone,
        null,
        mapLocation?.address?.clone() ?? null,
        mapLocation?.coordinates?.clone() ?? null,
        opts.profile.webRoute,
        dateComponent?.dateSpan ?? null,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ContactDetails {
    return new ContactDetails(
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
        toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
        toEnumFromString(MapUtils.get<string>(map, 'language'), Language),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber')),
        context.fromDataOrNull<Address>(MapUtils.getOrNull<SerializedData>(map, 'address')),
        context.fromDataOrNull<GeoCoordinates>(MapUtils.getOrNull<SerializedData>(map, 'coordinates')),
        MapUtils.getOrNull<string>(map, 'webRoute'),
        context.fromDataOrNull<DateSpan>(MapUtils.getOrNull<SerializedData>(map, 'dateSpan')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'names': context.toData(this.names),
      'country': fromEnumAsString(this.country),
      'language': fromEnumAsString(this.language),
      'timezone': fromEnumAsString(this.timezone),
      'phoneNumber': this.phoneNumber?.encode() ?? null,
      'address': context.toDataOrNull(this.address),
      'coordinates': context.toDataOrNull(this.coordinates),
      'webRoute': this.webRoute,
      'dateSpan': context.toDataOrNull(this.dateSpan),
    };
  }

  clone(): this {
    return new ContactDetails(
        this.names.clone(),
        this.country,
        this.language,
        this.timezone,
        this.phoneNumber?.clone() ?? null,
        this.address?.clone() ?? null,
        this.coordinates?.clone() ?? null,
        this.webRoute,
        this.dateSpan?.clone() ?? null,
    ) as this;
  }

  isSame(other: ContactDetails): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.names, this.names) &&
          isSameData(other.dateSpan, this.dateSpan) &&
          other.country === this.country &&
          other.language === this.language &&
          other.timezone === this.timezone &&
          other.phoneNumber === this.phoneNumber &&
          isSameData(other.address, this.address) &&
          isSameData(other.coordinates, this.coordinates) &&
          other.webRoute === this.webRoute;
  }
}

export class ContactDetailsBuilder extends DataModelBuilder<ContactDetails> {
  build(context: SerializationContext, map: SerializedData): ContactDetails {
    return ContactDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(ContactDetails.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(ContactDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `webRoute` of type `string | null` was introduced.
    if(version < 2) {
      version = 2;
    }

    // The [name] field was renamed to [names] and converted to a [LocalizedString].
    if(version < 3) {
      version = 3;

      const country: Country = toEnumFromString(MapUtils.get<string>(map, 'country'), Country);
      const language = context.storage.get<Language>('language') ?? getLanguageOfCountry(country) ?? Language.English;
      const name = MapUtils.get<string>(map, 'name');
      map['names'] = context.toData(LocalizedString.forLanguage(language, name));
    }

    // In this version parameter `language` of type `Language` was introduced.
    if(version < 4) {
      version = 4;

      const country: Country = toEnumFromString(MapUtils.get<string>(map, 'country'), Country);
      const language = context.storage.get<Language>('language') ?? getLanguageOfCountry(country) ?? Language.English;
      map['language'] = fromEnumAsString(language);
    }

    // In this version `dateSpan` of type `DateSpan | null` was introduced.
    if(version < 5) {
      version = 5;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
